import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { auth } from '../utils/api';
import toast from 'react-hot-toast';
import LocationPicker from '../components/LocationPicker';

export default function Profile() {
  const { user, setUser, t } = useApp();
  const navigate = useNavigate();

  const [form,    setForm]    = useState({ 
    fullName: user?.fullName ?? '', 
    bio: user?.bio ?? '',
    gender: user?.gender ?? '',
    locationId: user?.locationId ?? null
  });
  const [pwForm,  setPwForm]  = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [saving,  setSaving]  = useState(false);
  const [savingPw,setSavingPw]= useState(false);

  if (!user) { navigate('/login'); return null; }

  const set   = k => e => setForm(f  => ({ ...f,  [k]: e.target.value }));
  const setPw = k => e => setPwForm(f => ({ ...f, [k]: e.target.value }));

  const handleProfile = async e => {
    e.preventDefault();
    setSaving(true);
    try {
      const updated = await auth.updateProfile(form);
      setUser(prev => ({ ...prev, ...updated }));
      toast.success('Profile updated!');
    } catch (err) {
      toast.error(err.error || t('error_occurred'));
    } finally { setSaving(false); }
  };

  const handlePassword = async e => {
    e.preventDefault();
    if (pwForm.newPassword !== pwForm.confirmPassword) { toast.error('Passwords do not match'); return; }
    if (pwForm.newPassword.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setSavingPw(true);
    try {
      await auth.changePassword({ currentPassword: pwForm.currentPassword, newPassword: pwForm.newPassword });
      toast.success('Password changed!');
      setPwForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      toast.error(err.error || t('error_occurred'));
    } finally { setSavingPw(false); }
  };

  return (
    <div className="container-sm" style={{ paddingTop: '36px', paddingBottom: '60px' }}>
      <h1 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '24px' }}>{t('profile')}</h1>

      {/* Avatar & role */}
      <div className="card" style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '16px' }}>
        <div style={{
          width: 64, height: 64, borderRadius: '50%',
          background: 'var(--brand-light)', color: 'var(--brand-dark)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '26px', fontWeight: 700, flexShrink: 0,
        }}>
          {user.fullName?.[0]?.toUpperCase()}
        </div>
        <div>
          <div style={{ fontWeight: 700, fontSize: '18px' }}>{user.fullName}</div>
          <div style={{ fontSize: '13px', color: 'var(--text3)', marginTop: '2px' }}>{user.email}</div>
          <span className={`chip ${
            user.role === 'POSTER' ? 'chip-blue'
            : user.role === 'MODERATOR' ? 'chip-amber'
            : user.role === 'SUPERADMIN' ? 'chip-red'
            : 'chip-teal'
          }`} style={{ marginTop: '6px', display: 'inline-flex' }}>
            {user.role}
          </span>
        </div>
      </div>

      {/* Edit profile */}
      <form className="card" style={{ marginBottom: '16px' }} onSubmit={handleProfile}>
        <div style={{ fontSize: '15px', fontWeight: 600, marginBottom: '16px' }}>Edit profile</div>

        <div className="form-group">
          <label className="form-label">{t('name')}</label>
          <input className="form-input" required value={form.fullName} onChange={set('fullName')} />
        </div>
        
        <div className="form-grid-2">
          <div className="form-group">
            <label className="form-label">{t('email')}</label>
            <input className="form-input" value={user.email} disabled style={{ opacity: 0.6 }} />
          </div>
          <div className="form-group">
            <label className="form-label">{t('phone')}</label>
            <input className="form-input" value={user.phone} disabled style={{ opacity: 0.6 }} />
          </div>
        </div>

        <div className="form-grid-2">
          <div className="form-group">
            <label className="form-label">National ID Number</label>
            <input className="form-input" value={user.nidNumber || ''} disabled style={{ opacity: 0.6 }} />
          </div>
          <div className="form-group">
            <label className="form-label">Date of Birth</label>
            <input className="form-input" type="date" value={user.dob || ''} disabled style={{ opacity: 0.6 }} />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Gender</label>
          <select className="form-input" required value={form.gender} onChange={set('gender')}>
            <option value="">Select Gender</option>
            <option value="MALE">Male</option>
            <option value="FEMALE">Female</option>
            <option value="OTHER">Other</option>
          </select>
        </div>

        <div className="form-group">
          <label className="form-label">Location</label>
          <LocationPicker value={form.locationId} onChange={locId => setForm(f => ({ ...f, locationId: locId }))} />
        </div>
        <div className="form-group">
          <label className="form-label">{t('bio')} (optional)</label>
          <textarea className="form-input" rows={3} value={form.bio} onChange={set('bio')}
            placeholder="A short bio about yourself..." style={{ resize: 'vertical' }} />
        </div>
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? <span className="spinner" style={{ width: 16, height: 16 }} /> : 'Save changes'}
        </button>
      </form>

      {/* Change password */}
      <form className="card" onSubmit={handlePassword}>
        <div style={{ fontSize: '15px', fontWeight: 600, marginBottom: '16px' }}>Change password</div>
        <div className="form-group">
          <label className="form-label">Current password</label>
          <input className="form-input" type="password" required value={pwForm.currentPassword}
            onChange={setPw('currentPassword')} autoComplete="current-password" />
        </div>
        <div className="form-group">
          <label className="form-label">New password</label>
          <input className="form-input" type="password" required minLength={6} value={pwForm.newPassword}
            onChange={setPw('newPassword')} autoComplete="new-password" />
        </div>
        <div className="form-group">
          <label className="form-label">Confirm new password</label>
          <input className="form-input" type="password" required minLength={6} value={pwForm.confirmPassword}
            onChange={setPw('confirmPassword')} autoComplete="new-password" />
        </div>
        <button type="submit" className="btn btn-outline" disabled={savingPw}>
          {savingPw ? <span className="spinner" style={{ width: 16, height: 16 }} /> : 'Change password'}
        </button>
      </form>
    </div>
  );
}
